import { ReactNode, useRef, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";
import NotificationsBell from "@/components/NotificationsBell";

const NoAccess = ({ email, userId, signOut }: { email: string; userId: string; signOut: () => void }) => {
  const [claiming, setClaiming] = useState(false);

  const claim = async () => {
    setClaiming(true);
    const { data, error } = await supabase.rpc("claim_first_admin");
    setClaiming(false);
    if (error) return toast.error(error.message);
    if (data) {
      toast.success("You're now the first admin!");
      window.location.reload();
    } else {
      toast.error("An admin already exists. Ask them to grant you access.");
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(userId);
    toast.success("User ID copied");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-card rounded-[1.5rem] shadow-card p-10">
        <h1 className="font-display text-3xl">No access</h1>
        <p className="mt-3 text-muted-foreground">
          You're signed in but don't have editor access yet.
        </p>
        <p className="mt-3 text-sm">{email}</p>
        <button onClick={copyId} className="mt-1 text-xs text-muted-foreground hover:text-primary font-mono break-all">
          {userId} <span className="underline">(copy)</span>
        </button>

        <div className="mt-6 space-y-3">
          <button
            onClick={claim}
            disabled={claiming}
            className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {claiming ? "..." : "Claim admin (first user only)"}
          </button>
          <p className="text-xs text-muted-foreground">
            This works only if no admin exists yet. Otherwise, share your User ID with an admin to be granted access.
          </p>
        </div>

        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/" className="px-5 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold">
            Back to site
          </Link>
          <button onClick={signOut} className="px-5 py-2 rounded-full bg-muted text-sm font-semibold">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }: { children: ReactNode }) => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const location = useLocation();
  const resolvedOnce = useRef(false);
  if (!loading && user && isAdmin) resolvedOnce.current = true;

  if (loading && !resolvedOnce.current) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <NoAccess email={user.email ?? ""} userId={user.id} signOut={signOut} />;

  const tabs = [
    { to: "/admin", label: "Athletes", end: true },
    { to: "/admin/athletes/overview", label: "Athlete Overview" },
    { to: "/admin/signups", label: "Signups" },
    { to: "/admin/fans", label: "Fans" },
    { to: "/admin/updates", label: "Updates" },
    { to: "/admin/orders", label: "Orders" },
    { to: "/admin/review", label: "Review" },
    { to: "/admin/preview", label: "Preview" },
    { to: "/admin/bulk-import", label: "Bulk Import" },
    { to: "/admin/qr", label: "QR Codes" },
    { to: "/admin/teams", label: "Teams" },
    { to: "/admin/causes", label: "Causes" },
    { to: "/admin/products", label: "Products" },
    { to: "/admin/news", label: "News" },
    { to: "/admin/scores", label: "Scores" },
    { to: "/admin/blog", label: "Spotlight" },
    { to: "/admin/media", label: "Media Hub" },
    { to: "/admin/support", label: "Support" },
    { to: "/admin/content", label: "Site Content" },
    { to: "/admin/team", label: "Team" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container flex min-w-0 items-center justify-between gap-2 h-16">
          <div className="flex min-w-0 items-center gap-2 sm:gap-6">
            <Link to="/">
              <Wordmark size="sm" className="max-[380px]:[&_span]:hidden" />
            </Link>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Admin</span>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <NotificationsBell />
            <span className="text-xs text-muted-foreground hidden sm:inline">{user.email}</span>
            <button onClick={signOut} className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors">
              Sign out
            </button>
          </div>
        </div>
        <nav className="container flex max-w-full gap-1 overflow-x-auto">
          {tabs.map((t) => {
            const active = t.end ? location.pathname === t.to : location.pathname.startsWith(t.to);
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                  active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="container py-8">{children}</main>
    </div>
  );
};

export default AdminLayout;
